<script setup lang="ts">
    import Expense from '~/app/invoicing/models/Expense';
    import Worksite from '~/app/worksite/models/Worksite';
    import ExpenseSchema from '~/app/invoicing/schemas/ExpenseSchema';
    import EndpointFactory from '~/app/factories/EndpointFactory';
    import { ModelType } from '~/app/base/schemas/BaseSchema';

    const currentExpense = ref<Expense | null>(null);
    const form = useForm<{
        worksite: Worksite | null;
    }>({
        worksite: null,
    });

    const { modalName } = useModal(
        'invoicing:expense:add-worksite-modal',
        (expense: Expense) => {
            currentExpense.value = expense;
            form.set('worksite', expense.worksite);
        },
        () => {
            currentExpense.value = null;
            form.reset();
        }
    );

    const isLoading = ref(false);

    const submit = async () => {
        if (isLoading.value) return;
        isLoading.value = true;
        if (!currentExpense.value) return;

        const endpoint = EndpointFactory.make(ModelType.EXPENSES);
        const schema = new ExpenseSchema({
            attributes: {},
        });
        schema.id = currentExpense.value.getId();

        const worksite = form.get('worksite');
        const worksiteId = worksite && typeof worksite.getId === 'function' ? worksite.getId() : null;
        schema.addToOneRelationship('worksite', ModelType.WORKSITES, worksiteId);

        const response = await form.loadUntil(endpoint.update(schema));

        if (response.error) {
            if (!response.validationErrors) {
                useToasteoError();
            }
            return;
        }

        useToasteoSuccess('invoicing.expenses.updated_successfully');
        close();
        isLoading.value = false;
    };

    const close = () => {
        useEvent('modal:close', modalName);
    };
</script>

<template>
    <Modal
        :name="modalName"
        :width="500"
    >
        <div :class="$theme('modal.padding')">
            <div class="mt-4">
                <h2 :class="$theme('modal.titleLeft.text')">
                    {{ $t('invoicing.expense.add_worksite_modal.title') }}
                </h2>
            </div>

            <form
                class="mt-2"
                @submit.prevent="submit"
            >
                <FormWorksiteSelect
                    :form="form"
                    without-label
                    input-name="worksite"
                    with-create-button
                />
                <UiAction
                    :content="{
                        label: 'actions.save',
                        icon: 'fa-regular fa-check',
                        type: 'main',
                    }"
                    modifier="full"
                    :is-loading="isLoading"
                    @click="submit"
                />
            </form>
        </div>
    </Modal>
</template>
